import { useMemo } from "react";
import { useFlagOnaAIEnabled, useFlagOnaEnvironmentOnboardingEnabled } from "@/hooks/use-flag";
import {
    Agents,
    useAgentExecutionsByEnvironment,
    useGetAgentExecution,
    type PlainAgentExecution,
} from "@/queries/agent-queries";
import { useEnvironment } from "@/queries/environment-queries";
import { agentExecutionEnvironmentIds } from "@/utils/agent-executions";

export type ConversationPanelType =
    | {
          type: "onboarding";
      }
    | { type: "remote-agent-execution"; agentExecution: PlainAgentExecution }
    | { type: "in-environment-agent-execution"; agentExecution?: PlainAgentExecution };

/**
 * Extracts the agent execution from a conversation panel type.
 * Returns null if there's no conversation or no agent execution.
 */
export function getAgentExecution(conversation: ConversationPanelType | null): PlainAgentExecution | null {
    if (!conversation || conversation.type === "onboarding") return null;
    return conversation.agentExecution || null;
}

/**
 * Extracts the first PR URL from an agent execution's outputs.
 * Looks for keys matching `pr_url` or `pr_url_<number>`.
 */
export function getPrUrl(agentExecution: PlainAgentExecution | null): string | undefined {
    if (!agentExecution?.status?.outputs) return undefined;
    for (const key of Object.keys(agentExecution.status.outputs)) {
        if (key === "pr_url" || /^pr_url_\d+$/.test(key)) {
            const output = agentExecution.status.outputs[key];
            if (output?.value?.case === "stringValue" && output.value.value) {
                return output.value.value;
            }
        }
    }
    return undefined;
}

export const useEnvironmentConversation = (environmentId?: string, preferredAgentExecutionId?: string) => {
    const { data: environment, isPending: isEnvironmentPending } = useEnvironment(environmentId);
    const { data: preferredAgentExecution, isPending: isPreferredAgentExecutionPending } =
        useGetAgentExecution(preferredAgentExecutionId);

    const { data: onboardingAgentExecution, isPending: isOnboardingAgentExecutionPending } =
        useAgentExecutionsByEnvironment({
            environmentId,
            agentId: Agents.DevelopmentEnvironmentSetup.id,
        });

    const { data: remoteAgentExecution, isPending: isRemoteAgentExecutionPending } = useAgentExecutionsByEnvironment({
        environmentId,
        agentId: Agents.Ona.id,
    });

    const { data: inEnvironmentAgentExecution, isPending: isInEnvironmentAgentExecutionPending } =
        useAgentExecutionsByEnvironment({
            environmentId,
            agentId: Agents.InEnvironmentOna.id,
        });

    const { data: inEnvironmentClaudeCodeExecution, isPending: isInEnvironmentClaudeCodePending } =
        useAgentExecutionsByEnvironment({
            environmentId,
            agentId: Agents.InEnvironmentClaudeCode.id,
        });

    const { data: inEnvironmentCodexExecution, isPending: isInEnvironmentCodexPending } =
        useAgentExecutionsByEnvironment({
            environmentId,
            agentId: Agents.InEnvironmentCodex.id,
        });

    const { data: inEnvironmentGeminiExecution, isPending: isInEnvironmentGeminiPending } =
        useAgentExecutionsByEnvironment({
            environmentId,
            agentId: Agents.InEnvironmentGemini.id,
        });

    const { value: isOnaEnvironmentOnboardingEnabled } = useFlagOnaEnvironmentOnboardingEnabled();
    const { value: isOnaEnabled } = useFlagOnaAIEnabled();
    const isFeatureFlagEnabled = isOnaEnabled || isOnaEnvironmentOnboardingEnabled;

    // New prompt submission already knows the target execution ID. Prefer that
    // optimistic execution on first paint so the details page doesn't flash
    // through the empty conversation state while list queries catch up.
    const preferredConversation = useMemo((): ConversationPanelType | null => {
        if (!preferredAgentExecution || !environmentId) {
            return null;
        }

        if (!agentExecutionEnvironmentIds(preferredAgentExecution).includes(environmentId)) {
            return null;
        }

        if (preferredAgentExecution.spec?.agentId === Agents.Ona.id) {
            return {
                type: "remote-agent-execution",
                agentExecution: preferredAgentExecution,
            };
        }

        // All other agent types (InEnvironmentOna, ClaudeCode, Codex, Gemini,
        // and subagents) are treated as in-environment executions.
        return {
            type: "in-environment-agent-execution",
            agentExecution: preferredAgentExecution,
        };
    }, [preferredAgentExecution, environmentId]);

    const isPending = environmentId
        ? isEnvironmentPending ||
          (!!preferredAgentExecutionId && !preferredConversation && isPreferredAgentExecutionPending) ||
          isOnboardingAgentExecutionPending ||
          isRemoteAgentExecutionPending ||
          isInEnvironmentAgentExecutionPending ||
          isInEnvironmentClaudeCodePending ||
          isInEnvironmentCodexPending ||
          isInEnvironmentGeminiPending
        : false;

    const createdFromProject = environment?.metadata?.projectId;
    const showOnboardingConversation =
        isOnaEnvironmentOnboardingEnabled &&
        !isPending &&
        (!createdFromProject || onboardingAgentExecution !== undefined);

    const environmentConversation: ConversationPanelType | null = useMemo(() => {
        if (!isFeatureFlagEnabled || !environmentId) {
            return null;
        }

        if (preferredConversation) {
            return preferredConversation;
        }

        if (isPending) {
            return null;
        }

        if (remoteAgentExecution !== undefined) {
            return {
                type: "remote-agent-execution",
                agentExecution: remoteAgentExecution,
            };
        }

        if (inEnvironmentAgentExecution !== undefined) {
            return {
                type: "in-environment-agent-execution",
                agentExecution: inEnvironmentAgentExecution,
            };
        }

        if (inEnvironmentClaudeCodeExecution !== undefined) {
            return {
                type: "in-environment-agent-execution",
                agentExecution: inEnvironmentClaudeCodeExecution,
            };
        }

        if (inEnvironmentCodexExecution !== undefined) {
            return {
                type: "in-environment-agent-execution",
                agentExecution: inEnvironmentCodexExecution,
            };
        }

        if (inEnvironmentGeminiExecution !== undefined) {
            return {
                type: "in-environment-agent-execution",
                agentExecution: inEnvironmentGeminiExecution,
            };
        }

        if (showOnboardingConversation) {
            return {
                type: "onboarding",
            };
        }

        return null;
    }, [
        isFeatureFlagEnabled,
        environmentId,
        preferredConversation,
        isPending,
        remoteAgentExecution,
        inEnvironmentAgentExecution,
        inEnvironmentClaudeCodeExecution,
        inEnvironmentCodexExecution,
        inEnvironmentGeminiExecution,
        showOnboardingConversation,
    ]);

    return {
        environmentConversation,
        isPending,
    };
};
